import { useQuery } from '@tanstack/react-query';
import { mediaApi } from '@/api/media';
import { useAuthStore } from '@/store/authStore';

export type SignedMediaType = 'thumbnail' | 'download' | 'raw' | 'playlist.m3u8';

const SIGNED_URL_REFRESH_MS = 4 * 60 * 1000;
const SIGNED_URL_SAFETY_MS = 30 * 1000;

function hasUsableLifetime(value: string): boolean {
  try {
    const exp = Number(new URL(value, window.location.origin).searchParams.get('exp'));
    return Number.isFinite(exp) && exp * 1000 > Date.now() + SIGNED_URL_SAFETY_MS;
  } catch {
    return false;
  }
}

/**
 * Кешированный signed URL для одного media-файла.
 *
 * Фоновое поведение:
 *  - При первом use компонент отправляет POST /api/media/sign на 1 id.
 *  - React Query кеширует результат на staleTime=4 минуты (signed URL живёт
 *    5 мин, refresh за 1 мин до expire — гарантия что юзер не получит 401).
 *  - Cache shared между components — несколько `<MessageItem>` с одинаковым
 *    media_id отправят только 1 запрос, остальные подхватят кеш.
 *
 * Возвращает `null` пока запрос летит (caller должен показать placeholder/
 * skeleton). Возвращает строку URL когда готов.
 */
export function useSignedMediaUrl(
  mediaFileId: string | null | undefined,
  type: SignedMediaType,
): string | null {
  const accessToken = useAuthStore((s) => s.accessToken);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const { data } = useQuery({
    // Bind the cache to the account. A rapid logout/login in the same tab
    // must never reuse the previous user's still-valid signed URL.
    queryKey: ['mediaSign', userId, mediaFileId, type],
    queryFn: async () => {
      if (!mediaFileId) return null;
      const map = await mediaApi.signMedia([mediaFileId], [type]);
      return map[mediaFileId]?.[type] ?? null;
    },
    enabled: !!mediaFileId && !!accessToken && !!userId,
    staleTime: SIGNED_URL_REFRESH_MS,
    gcTime: 5 * 60 * 1000, // 5 мин
    // staleTime alone does not schedule a refetch for a mounted component.
    // Refresh before the server's five-minute signature expires, and refresh
    // immediately after a suspended/background tab becomes usable again.
    refetchInterval: SIGNED_URL_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });
  return data && hasUsableLifetime(data) ? data : null;
}
